import { Link } from '@tanstack/react-router';
import { ArrowLeft, Rocket, Lightbulb, Target, Zap, Heart, BookOpen, Sparkles } from 'lucide-react';
import { useTranslation } from '@/hooks/useTypedTranslation';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { PageLayout } from '@/components/PageLayout';
import { Meta } from '@/components/MetaManager';
import { StructuredData, generateBreadcrumbs } from '@/components/StructuredData';

export default function AboutEntrepreneur() {
  const { t, i18n } = useTranslation();
  const { getLocalizedPath } = useLocalizedPath();
  const lang = i18n.language as 'en' | 'zh';

  const breadcrumbs = generateBreadcrumbs([
    { label: lang === 'zh' ? '关于我' : 'About', path: '/about' },
    { label: lang === 'zh' ? '创业者' : 'Entrepreneur', path: '/about/entrepreneur' }
  ], lang);

  return (
    <PageLayout>
      <Meta
        title={`${t.about.entrepreneur.title} | borninsea`}
        description={t.about.entrepreneur.subtitle}
        keywords="创业者,AI创业,产品经理,独立开发,创业经历"
      />
      <StructuredData type="profile" breadcrumbs={breadcrumbs} />
      <div className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 rounded-full bg-blue-500/20 border border-blue-500/30">
                <Rocket className="w-8 h-8 text-blue-400" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold">{t.about.entrepreneur.title}</h1>
                <p className="text-muted-foreground">{t.about.entrepreneur.subtitle}</p>
              </div>
            </div>
          </div>

        {/* Intro */}
        <section className="mb-16">
          {t.about.entrepreneur.intro.map((paragraph, index) => (
            <p key={index} className={`${index === 0 ? 'text-xl' : 'text-lg'} ${index === 1 ? 'font-medium' : ''} text-foreground/80 leading-relaxed mb-6`}>
              {paragraph}
            </p>
          ))}
        </section>

        {/* Key Projects */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-8">{t.about.entrepreneur.keyProjects.title}</h2>
          <div className="space-y-6">
            {t.about.entrepreneur.keyProjects.projects.map((project, index) => {
              const icons = [Heart, BookOpen, Sparkles];
              const Icon = icons[index];
              return (
                <div 
                  key={project.title}
                  className="p-6 rounded-xl bg-foreground/5 border border-foreground/10"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-blue-500/20">
                      <Icon className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold">{project.title}</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-foreground/10 text-muted-foreground">
                          {project.year}
                        </span>
                      </div>
                      <p className="text-foreground/70">{project.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Principles */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-8">{t.about.entrepreneur.principles.title}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {t.about.entrepreneur.principles.items.map((principle, index) => {
              const icons = [Lightbulb, Target, Zap];
              const Icon = icons[index];
              return (
                <div 
                  key={principle.title}
                  className="p-6 rounded-xl bg-blue-500/10 border border-blue-500/20"
                >
                  <Icon className="w-8 h-8 text-blue-400 mb-4" />
                  <h3 className="text-lg font-bold mb-2">{principle.title}</h3>
                  <p className="text-foreground/70 text-sm">{principle.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Ventures */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-8">{t.about.entrepreneur.ventures.title}</h2>
          <div className="space-y-4">
            {t.about.entrepreneur.ventures.items.map((venture) => (
              <Link
                key={venture.title}
                to={getLocalizedPath(`/${venture.title.toLowerCase().replace(' ', '')}`)}
                className="block p-6 rounded-xl bg-foreground/5 border border-foreground/10 hover:border-blue-500/30 hover:bg-blue-500/5 transition-all duration-300 group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold mb-1 group-hover:text-blue-400 transition-colors">
                      {venture.title}
                    </h3>
                    <p className="text-foreground/70 text-sm">{venture.description}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs px-3 py-1 rounded-full bg-blue-500/20 text-blue-400">
                      {venture.status}
                    </span>
                    <ArrowLeft className="w-4 h-4 rotate-180 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Philosophy */}
        <section className="p-8 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20">
          <h2 className="text-2xl font-bold mb-6">{t.about.entrepreneur.philosophy.title}</h2>
          <div className="space-y-4 text-foreground/80">
            {t.about.entrepreneur.philosophy.content.map((paragraph, index) => (
              <p key={index} className={index === 2 ? "font-medium text-foreground" : ""}>
                {paragraph}
              </p>
            ))}
          </div>
        </section>
        </div>
      </div>
    </PageLayout>
  );
}
